import React, { createContext, useMemo, useRef, useCallback } from 'react';
import useCopilot from './hooks/useCopilot';

/**
 * Context for Copilot feature registration and management.
 *
 * This context provides a centralized system for:
 * - Registering feature-specific methods that can be invoked by Copilot
 * - Managing function registries for AI tool calling
 * - Accessing Copilot state and functionality throughout the application
 *
 * @type {React.Context<{
 *   featureMethods: Object.<string, Object>,
 *   functionRegistry: Object.<string, Function>,
 *   getMethodsForCopilot: {
 *     registerFeatureMethods: Function,
 *     registerFunctions: Function
 *   }
 * }>}
 */
const CopilotContext = createContext();

const initialMethods = {};
const initialFunctionRegistry = {};

/**
 * Provider component for Copilot features.
 *
 * Wraps the application (or a section of it) to provide Copilot functionality
 * and feature registration capabilities to all child components.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to Copilot context
 * @param {Object} [props.rest] - Additional props to be spread into the context value
 * @returns {JSX.Element} Provider component wrapping children
 *
 * @example
 * <CopilotProvider>
 *   <App />
 * </CopilotProvider>
 */
const CopilotProvider = ({ children, ...props }) => {
    const featureMethods = useRef(initialMethods);
    const functionRegistry = useRef(initialFunctionRegistry);
    const copilotState = useCopilot();

    /**
     * Register methods for a specific feature type.
     *
     * This function allows components to register domain-specific methods that
     * can be invoked by Copilot. Methods are organized by feature type for better
     * organization and to prevent naming conflicts.
     *
     * Multiple calls with the same featureType will merge the methods, allowing
     * different components to contribute methods to the same feature namespace.
     *
     * @function
     * @param {string} featureType - Type of feature (e.g., 'forceBI', 'analytics', 'userManagement')
     * @param {Object.<string, Function>} methods - Object containing methods to register
     *
     * @example
     * registerFeatureMethods('analytics', {
     *   trackEvent: (eventName, data) => {
     *     console.log(`Tracking ${eventName}:`, data);
     *   },
     *   pageView: (pageName) => {
     *     console.log(`Page view: ${pageName}`);
     *   }
     * });
     */
    const registerFeatureMethods = useCallback((featureType, methods = {}) => {
        featureMethods.current[featureType] = {
            ...(featureMethods.current[featureType] || {}),
            ...methods,
        };
    }, []);

    /**
     * Register functions available to be called as tools from Copilot.
     *
     * This function registers executable functions that the AI can invoke during
     * conversations. These functions become available as "tools" that Copilot can
     * call to perform actions or retrieve information.
     *
     * Each function should be well-documented and handle errors gracefully, as they
     * will be invoked by the AI based on user intent.
     *
     * @function
     * @param {Object.<string, Function>} functions - Object containing functions to register as AI tools
     *
     * @example
     * registerFunctions({
     *   searchUsers: async (query) => {
     *     const response = await fetch(`/api/users?q=${query}`);
     *     return response.json();
     *   },
     *   updateUserProfile: async (userId, data) => {
     *     const response = await fetch(`/api/users/${userId}`, {
     *       method: 'PUT',
     *       body: JSON.stringify(data)
     *     });
     *     return response.json();
     *   }
     * });
     */
    const registerFunctions = useCallback((functions = {}) => {
        functionRegistry.current = {
            ...functionRegistry.current,
            ...functions,
        };
    }, []);

    /**
     * Memoized object containing registration methods for Copilot.
     *
     * This object is provided to child components through the context and allows
     * them to register their methods and functions with Copilot.
     *
     * @type {Object}
     * @property {Function} registerFeatureMethods - Register feature-specific methods
     * @property {Function} registerFunctions - Register AI-callable functions
     */
    const getMethodsForCopilot = useMemo(
        () => ({
            registerFeatureMethods,
            registerFunctions,
        }),
        [registerFeatureMethods, registerFunctions],
    );

    return (
        <CopilotContext.Provider
            value={{
                ...copilotState,
                getMethodsForCopilot,
                featureMethods: featureMethods.current,
                functionRegistry: functionRegistry.current,
                ...props,
            }}
        >
            {children}
        </CopilotContext.Provider>
    );
};

/**
 * Consumer component for Copilot context.
 *
 * Use this for accessing Copilot context in class components or when render props
 * pattern is preferred. For functional components, use the `useContext` hook instead.
 *
 * @type {React.Consumer}
 *
 * @example
 * <CopilotConsumer>
 *   {({ featureMethods }) => (
 *     <button onClick={() => featureMethods.analytics?.trackEvent('click')}>
 *       Track Event
 *     </button>
 *   )}
 * </CopilotConsumer>
 */
const CopilotConsumer = CopilotContext.Consumer;

export { CopilotContext, CopilotProvider, CopilotConsumer };

/**
 * ============================================================================
 * USAGE EXAMPLES
 * ============================================================================
 *
 * ## Basic Setup
 *
 * ```jsx
 * import React from 'react';
 * import { CopilotProvider } from '@Sage/ssm-web5';
 *
 * const App = () => (
 *   <CopilotProvider>
 *     <YourApp />
 *   </CopilotProvider>
 * );
 * ```
 *
 * ## Registering Feature Methods
 *
 * Feature methods are domain-specific functions that organize related functionality.
 * Use this pattern when you want to group methods by business domain.
 *
 * ```jsx
 * import React, { useContext, useEffect } from 'react';
 * import { CopilotContext } from '@Sage/ssm-web5';
 *
 * const AnalyticsFeature = () => {
 *   const { getMethodsForCopilot } = useContext(CopilotContext);
 *
 *   useEffect(() => {
 *     // Register analytics-related methods
 *     getMethodsForCopilot.registerFeatureMethods('analytics', {
 *       trackEvent: (eventName, data) => {
 *         console.log(`Tracking ${eventName}:`, data);
 *         // Send to analytics service
 *       },
 *       pageView: (pageName) => {
 *         console.log(`Page view: ${pageName}`);
 *         // Track page view
 *       },
 *       identifyUser: (userId, traits) => {
 *         console.log(`Identifying user ${userId}`, traits);
 *         // Identify user in analytics
 *       }
 *     });
 *   }, [getMethodsForCopilot]);
 *
 *   return <div>Analytics Feature Active</div>;
 * };
 *
 * // Register another feature in a different component
 * const ForceBIFeature = () => {
 *   const { getMethodsForCopilot } = useContext(CopilotContext);
 *
 *   useEffect(() => {
 *     getMethodsForCopilot.registerFeatureMethods('forceBI', {
 *       sendData: async (payload) => {
 *         const response = await fetch('/api/forcebi', {
 *           method: 'POST',
 *           body: JSON.stringify(payload)
 *         });
 *         return response.json();
 *       },
 *       refreshDashboard: (dashboardId) => {
 *         // Refresh BI dashboard
 *       }
 *     });
 *   }, [getMethodsForCopilot]);
 *
 *   return <div>ForceBI Feature Active</div>;
 * };
 * ```
 *
 * ## Using Registered Feature Methods
 *
 * Access and invoke registered methods from any component within the provider tree.
 *
 * ```jsx
 * const ConsumingComponent = () => {
 *   const { featureMethods } = useContext(CopilotContext);
 *
 *   const handleButtonClick = () => {
 *     // Safely access and call registered methods
 *     if (featureMethods.analytics?.trackEvent) {
 *       featureMethods.analytics.trackEvent('button_click', {
 *         button: 'submit',
 *         page: 'user-profile'
 *       });
 *     }
 *   };
 *
 *   const handleDashboardRefresh = async () => {
 *     if (featureMethods.forceBI?.refreshDashboard) {
 *       await featureMethods.forceBI.refreshDashboard('dashboard-123');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleButtonClick}>Track Event</button>
 *       <button onClick={handleDashboardRefresh}>Refresh Dashboard</button>
 *     </div>
 *   );
 * };
 * ```
 *
 * ## Registering AI-Callable Functions
 *
 * Register functions that the AI can invoke as tools during conversations.
 * These functions should be pure, well-documented, and handle errors gracefully.
 *
 * ```jsx
 * const UserManagementFeature = () => {
 *   const { getMethodsForCopilot } = useContext(CopilotContext);
 *
 *   useEffect(() => {
 *     getMethodsForCopilot.registerFunctions({
 *       // Search users by query
 *       searchUsers: async (query) => {
 *         try {
 *           const response = await fetch(`/api/users?q=${encodeURIComponent(query)}`);
 *           if (!response.ok) throw new Error('Search failed');
 *           return await response.json();
 *         } catch (error) {
 *           console.error('Error searching users:', error);
 *           return { error: error.message };
 *         }
 *       },
 *
 *       // Get user details
 *       getUserById: async (userId) => {
 *         try {
 *           const response = await fetch(`/api/users/${userId}`);
 *           if (!response.ok) throw new Error('User not found');
 *           return await response.json();
 *         } catch (error) {
 *           console.error('Error fetching user:', error);
 *           return { error: error.message };
 *         }
 *       },
 *
 *       // Update user profile
 *       updateUserProfile: async (userId, updates) => {
 *         try {
 *           const response = await fetch(`/api/users/${userId}`, {
 *             method: 'PUT',
 *             headers: { 'Content-Type': 'application/json' },
 *             body: JSON.stringify(updates)
 *           });
 *           if (!response.ok) throw new Error('Update failed');
 *           return await response.json();
 *         } catch (error) {
 *           console.error('Error updating user:', error);
 *           return { error: error.message };
 *         }
 *       }
 *     });
 *   }, [getMethodsForCopilot]);
 *
 *   return <div>User Management Tools Registered</div>;
 * };
 * ```
 *
 * ## Accessing Function Registry
 *
 * The AI system will access registered functions through the functionRegistry,
 * but you can also manually invoke them if needed.
 *
 * ```jsx
 * const CopilotHandler = () => {
 *   const { functionRegistry } = useContext(CopilotContext);
 *
 *   const handleManualInvoke = async () => {
 *     if (functionRegistry.searchUsers) {
 *       const results = await functionRegistry.searchUsers('john@example.com');
 *       console.log('Search results:', results);
 *     }
 *   };
 *
 *   return <button onClick={handleManualInvoke}>Manual Search</button>;
 * };
 * ```
 *
 * ## Best Practices
 *
 * 1. **Register in useEffect**: Always register methods inside useEffect to avoid
 *    repeated registrations on every render.
 *
 * 2. **Error Handling**: AI-callable functions should handle errors gracefully
 *    and return structured error objects rather than throwing.
 *
 * 3. **Namespace Organization**: Use clear, domain-specific feature types to
 *    organize methods logically (e.g., 'analytics', 'userManagement', 'forceBI').
 *
 * 4. **Safe Access**: Always use optional chaining (?.) when accessing registered
 *    methods to prevent runtime errors if a feature isn't registered.
 *
 * 5. **Documentation**: Document AI-callable functions thoroughly, including
 *    expected parameters and return values, as the AI will use them dynamically.
 *
 * 6. **Async Operations**: Prefer async/await for functions that perform I/O
 *    operations or API calls.
 */
